import { useState } from 'react';
import { toast } from 'react-toastify';
import { PATHS } from 'src/common/constants';
import {
  deleteUnit,
  fetchCalendlyLink,
  fetchFaqsNotesDocuments,
  fetchPropertyManagerDetailsForHomeowner,
  fetchSuiteCounts,
  fetchSuiteWalkThroughs,
  getAllowedTabList,
  getAllRoles,
  getAssetsBySuiteId,
  getBuildingLevels,
  getIndividualPropertySuiteByID,
  getProperties,
  getPropertyBuildings,
  getPropertySuiteById,
  onFetchMoreDetails,
  reRenderKeyPickupPdf,
  updateSuite,
} from 'src/services/client';
import {
  Asset,
  Contact,
  Counts,
  NoteDocument,
  Property,
  Suite,
  UserRole,
  WalkThrough,
} from './types';
import { ModulePermission } from 'src/components/ContactForm/types';

export interface SuiteActionsState {
  isLoading: boolean;
  isFormLoading: boolean;
  error: string | null;
}

export interface CalendlyData {
  calendlyAPIKey: string;
  calendlyLink: string;
}

export interface UseSuiteActionsReturn {
  // State.
  isLoading: boolean;
  isFormLoading: boolean;
  error: string | null;

  // State management.
  setLoading: (loading: boolean) => void;
  setFormLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearState: () => void;

  // API methods.
  fetchBuildingForProperty: (propertyId: string) => Promise<any[]>;
  fetchLevelsForBuilding: (buildingId: any) => Promise<any>;
  fetchSuiteById: (suiteId: string) => Promise<Suite | null>;
  fetchSuiteCounts: (suiteId: string, role: string) => Promise<Counts | null>;
  fetchPropertySuites: (
    propertyId: string,
    userId: string,
    role: string
  ) => Promise<Suite[]>;
  fetchProperties: (userId: string, role: string) => Promise<Property[]>;
  fetchAssetsBySuiteId: (
    suiteId: string,
    userId: string,
    role: string
  ) => Promise<Asset[]>;
  fetchSuiteWalkThroughs: (
    suiteId: string,
    roleName: string,
    flag?: boolean
  ) => Promise<WalkThrough[]>;
  fetchFaqsNotesDocuments: (suiteId: string) => Promise<NoteDocument[]>;
  fetchPropertyManagerDetailsForHomeowner: (
    propertyId: string
  ) => Promise<Contact | null>;
  fetchCalendlyLink: (email: string) => Promise<CalendlyData | null>;
  fetchAllowedTabList: (
    propertyId: string,
    branding: any
  ) => Promise<ModulePermission[]>;
  getAllUserTypeRoles: () => Promise<UserRole[]>;
  updateSuite: (suiteData: any) => Promise<Suite | null>;
  deleteUnit: (
    unitId: string,
    userId: string,
    history: any,
    property: Property
  ) => Promise<boolean>;
  fetchMoreDetails: (suiteId: string) => Promise<any | null>;
  reRenderKeyPickupPdf: (suiteId: string) => Promise<boolean>;
}

export const useSuiteActions = (): UseSuiteActionsReturn => {
  const [state, setState] = useState<SuiteActionsState>({
    isLoading: false,
    isFormLoading: false,
    error: null,
  });

  const setLoading = (loading: boolean) => {
    setState((prev: SuiteActionsState) => ({ ...prev, isLoading: loading }));
  };

  const setFormLoading = (loading: boolean) => {
    setState((prev: SuiteActionsState) => ({
      ...prev,
      isFormLoading: loading,
    }));
  };

  const setError = (error: string | null) => {
    setState((prev: SuiteActionsState) => ({ ...prev, error }));
  };

  const clearState = () => {
    setState({
      isLoading: false,
      isFormLoading: false,
      error: null,
    });
  };

  const fetchSuiteById = async (suiteId: string): Promise<Suite | null> => {
    try {
      setLoading(true);
      setError(null);

      const response: any = await getPropertySuiteById(suiteId);

      if (!response.ok) {
        throw new Error(`Failed to fetch suite: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to fetch suite details';
      setError(errorMessage);
      toast.error(`🚫 ${errorMessage}`);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchBuildingForProperty = async (
    propertyId: string
  ): Promise<any[]> => {
    try {
      setState((prev: SuiteActionsState) => ({
        ...prev,
        isLoading: true,
      }));

      const response: any = await getPropertyBuildings(propertyId);

      if (!response.ok) {
        throw new Error(`Failed to fetch buildings: ${response.status}`);
      }

      const result: any = await response.json();

      return Array.isArray(result) ? result : result?.data || [];
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to fetch buildings';
      console.error('fetchBuildingForProperty error:', error);
      toast.error(`🚫 ${errorMessage}`);

      setState((prev: SuiteActionsState) => ({
        ...prev,
        isLoading: false,
      }));

      return [];
    } finally {
      setState((prev: SuiteActionsState) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  const fetchLevelsForBuilding = async (buildingId: any) => {
    try {
      setState((prev: SuiteActionsState) => ({
        ...prev,
        isLoading: true,
      }));

      const response: any = await getBuildingLevels(buildingId);

      if (!response.ok) {
        throw new Error(
          `Failed to fetch levels for ${buildingId}: ${response.status}`
        );
      }

      return await response.json();
    } catch (error) {
      const errorMessage =
        error.message || 'Failed to fetch levels for buildings';
      console.error('fetchBuildingForProperty error:', error);
      toast.error(`🚫 ${errorMessage}`);

      setState((prev: SuiteActionsState) => ({
        ...prev,
        isLoading: false,
      }));

      return null;
    } finally {
      setState((prev: SuiteActionsState) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  const fetchSuiteCountsFromAPI = async (
    suiteId: string,
    role: string
  ): Promise<Counts | null> => {
    try {
      const response: any = await fetchSuiteCounts(suiteId, role);

      if (!response.ok) {
        throw new Error(`Failed to fetch suite counts: ${response.status}`);
      }

      const result = await response.json();

      if (result.statusCode === 200) {
        return result.data;
      } else {
        throw new Error('Invalid response from suite counts API');
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to fetch suite counts';
      console.error('fetchSuiteCounts error:', error);
      toast.error(`🚫 ${errorMessage}`);
      return null;
    }
  };

  const fetchPropertySuites = async (
    propertyId: string,
    userId: string,
    role: string
  ): Promise<Suite[]> => {
    try {
      setState((prev: SuiteActionsState) => ({
        ...prev,
        isLoading: true,
      }));

      const response: any = await getIndividualPropertySuiteByID(
        propertyId,
        userId,
        role
      );

      if (response.status === 200) {
        const result = await response.json();
        return Array.isArray(result) ? result : result?.data || [];
      } else if (response.status === 204) {
        return [];
      } else {
        throw new Error(`Failed to fetch property suites: ${response.status}`);
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to fetch property suites';
      console.error('fetchPropertySuites error:', error);
      toast.error(`🚫 ${errorMessage}`);
      return [];
    } finally {
      setState((prev: SuiteActionsState) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  const fetchProperties = async (
    userId: string,
    role: string
  ): Promise<Property[]> => {
    try {
      setState((prev: SuiteActionsState) => ({
        ...prev,
        isLoading: true,
      }));

      const response: any = await getProperties(userId, role);

      if (!response.ok) {
        throw new Error(`Failed to fetch properties: ${response.status}`);
      }

      const result: any = await response.json();
      return Array.isArray(result) ? result : result?.data || [];
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to fetch properties';
      console.error('fetchProperties error:', error);
      toast.error(`🚫 ${errorMessage}`);
      return [];
    } finally {
      setState((prev: SuiteActionsState) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  const fetchAssetsBySuiteId = async (
    suiteId: string,
    userId: string,
    role: string
  ): Promise<Asset[]> => {
    try {
      setState((prev: SuiteActionsState) => ({
        ...prev,
        isLoading: true,
      }));

      const response: any = await getAssetsBySuiteId(suiteId, userId, role);

      if (!response.ok) {
        throw new Error(`Failed to fetch assets: ${response.status}`);
      }

      const result: any = await response.json();
      return Array.isArray(result) ? result : result?.data || [];
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to fetch assets';
      console.error('fetchAssetsBySuiteId error:', error);
      toast.error(`🚫 ${errorMessage}`);
      return [];
    } finally {
      setState((prev: SuiteActionsState) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  const fetchSuiteWalkThroughsFromAPI = async (
    suiteId: string,
    roleName: string,
    flag?: boolean
  ): Promise<WalkThrough[]> => {
    try {
      setState((prev: SuiteActionsState) => ({
        ...prev,
        isLoading: true,
      }));

      const response: any = await fetchSuiteWalkThroughs(suiteId, roleName);

      if (!response.ok) {
        throw new Error(`Failed to fetch walkthroughs: ${response.status}`);
      }

      const result: any = await response.json();
      return Array.isArray(result) ? result : result?.data || [];
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to fetch walkthroughs';
      console.error('fetchSuiteWalkThroughs error:', error);
      toast.error(`🚫 ${errorMessage}`);
      return [];
    } finally {
      setState((prev: SuiteActionsState) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  const fetchFaqsNotesDocumentsFromAPI = async (
    suiteId: string
  ): Promise<NoteDocument[]> => {
    try {
      if (!suiteId) return [];

      const response: any = await fetchFaqsNotesDocuments(suiteId);

      if (!response.ok) {
        throw new Error(`Failed to fetch documents: ${response.status}`);
      }

      const result = await response.json();
      return Array.isArray(result) ? result : result?.data || [];
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to fetch documents';
      console.error('fetchFaqsNotesDocuments error:', error);
      toast.error(`🚫 ${errorMessage}`);
      return [];
    }
  };

  const fetchPropertyManagerDetailsForHomeownerFromAPI = async (
    propertyId: string
  ): Promise<Contact | null> => {
    try {
      const response: any =
        await fetchPropertyManagerDetailsForHomeowner(propertyId);

      if (!response.ok) {
        throw new Error(
          `Failed to fetch property manager details: ${response.status}`
        );
      }

      const result = await response.json();
      return result?.data || result || null;
    } catch (error: any) {
      const errorMessage =
        error.message || 'Failed to fetch property manager details';
      console.error('fetchPropertyManagerDetails error:', error);
      toast.error(`🚫 ${errorMessage}`);
      return null;
    }
  };

  const fetchCalendlyLinkFromAPI = async (
    email: string
  ): Promise<any | null> => {
    try {
      const response: any = await fetchCalendlyLink(email);

      if (!response.ok) {
        throw new Error(`Failed to fetch calendly link: ${response.status}`);
      }

      const result: any = await response.json();

      // Match Redux behavior: extract and rename fields.
      if (result.statusCode === 200) {
        if (result.data) {
          return {
            calendlyLink: result.data.calendarLink,
            calendlyAPIKey: result.data.calendlyAPIKey,
          };
        } else {
          return { calendlyLink: '' };
        }
      } else if (result.statusCode === 400) {
        return { calendlyLink: '' };
      }

      return { calendlyLink: '' };
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to fetch calendly link';
      console.error('fetchCalendlyLink error:', error);
      toast.error(`🚫 ${errorMessage}`);
      return { calendlyLink: '' };
    }
  };

  const fetchAllowedTabListFromAPI = async (
    propertyId: string,
    branding: any
  ): Promise<any[]> => {
    try {
      const response: any = await getAllowedTabList(propertyId);

      if (!response.ok) {
        throw new Error(`Failed to fetch tab list: ${response.status}`);
      }

      const result: any = await response.json();
      return Array.isArray(result) ? result : result?.data || [];
    } catch (error: any) {
      console.error('fetchAllowedTabList error:', error);
      return [];
    }
  };

  const getAllUserTypeRoles = async (): Promise<any[]> => {
    try {
      const response: any = await getAllRoles();

      if (!response.ok) {
        throw new Error(`Failed to fetch user roles: ${response.status}`);
      }

      const result = await response.json();
      return Array.isArray(result) ? result : result?.data || [];
    } catch (error: any) {
      console.error('getAllUserTypeRoles error:', error);
      return [];
    }
  };

  const updateSuiteFromAPI = async (suiteData: any): Promise<any | null> => {
    try {
      setFormLoading(true);
      setError(null);

      const response: any = await updateSuite(suiteData);

      if (!response.ok) {
        throw new Error(`Failed to update suite: ${response.status}`);
      }

      const result: any = await response.json();

      if (result.statusCode === 402) {
        throw new Error('That unit number already exists, please try another.');
      } else if (result.statusCode === 409 || result.statusCode === 400) {
        throw new Error('User already exists with this email address.');
      } else if (result.statusCode === 200) {
        toast.success('ⓘ Suite has been updated.');
        return result.data;
      } else {
        throw new Error('Something went wrong with updating the suite.');
      }
    } catch (error: any) {
      const errorMessage: any = error.message || 'Failed to update suite';
      setError(errorMessage);
      return null;
    } finally {
      setFormLoading(false);
    }
  };

  const deleteUnitFromAPI = async (
    unitId: string,
    userId: string,
    history: any,
    property: any
  ): Promise<boolean> => {
    try {
      setFormLoading(true);
      setError(null);

      const response: any = await deleteUnit(unitId, userId);

      if (!response.ok) {
        throw new Error(`Failed to delete unit: ${response.status}`);
      }

      toast.success('ⓘ Unit has been deleted.');

      if (history && property) {
        history.push({
          pathname: PATHS.PROPERTIES_VIEW.replace(':id', property.id),
          state: { data: property },
        });
      }

      return true;
    } catch (error: any) {
      const errorMessage: any = error.message || 'Failed to delete unit';
      setError(errorMessage);
      return false;
    } finally {
      setFormLoading(false);
    }
  };

  const fetchMoreDetailsFromAPI = async (
    suiteId: string
  ): Promise<any | null> => {
    try {
      setLoading(true);
      setError(null);

      const response: any = await onFetchMoreDetails(suiteId);

      if (!response.ok) {
        throw new Error(`Failed to fetch more details: ${response.status}`);
      }

      const result: any = await response.json();
      return result?.data || result || null;
    } catch (error: any) {
      const errorMessage: any = error.message || 'Failed to fetch more details';
      setError(errorMessage);
      console.error('fetchMoreDetails error:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const reRenderKeyPickupPdfFromAPI = async (
    suiteId: string
  ): Promise<boolean> => {
    try {
      setFormLoading(true);
      setError(null);

      const response: any = await reRenderKeyPickupPdf(suiteId);

      if (response.status === 200 || response.ok) {
        toast.success('ⓘ Key Pickup Confirmation re-sent');
        return true;
      } else {
        throw new Error(
          `KeyHandOff process submit failed. (${response.status})`
        );
      }
    } catch (error: any) {
      const errorMessage =
        error.message || 'Failed to re-render key pickup PDF';
      setError(errorMessage);
      return false;
    } finally {
      setFormLoading(false);
    }
  };

  return {
    // State + State management.
    ...state,
    setLoading,
    setFormLoading,
    setError,
    clearState,

    // API methods - all return clean, unwrapped data.
    fetchBuildingForProperty,
    fetchLevelsForBuilding,
    fetchSuiteById,
    fetchSuiteCounts: fetchSuiteCountsFromAPI,
    fetchPropertySuites,
    fetchProperties,
    fetchAssetsBySuiteId,
    fetchSuiteWalkThroughs: fetchSuiteWalkThroughsFromAPI,
    fetchFaqsNotesDocuments: fetchFaqsNotesDocumentsFromAPI,
    fetchPropertyManagerDetailsForHomeowner:
      fetchPropertyManagerDetailsForHomeownerFromAPI,
    fetchCalendlyLink: fetchCalendlyLinkFromAPI,
    fetchAllowedTabList: fetchAllowedTabListFromAPI,
    getAllUserTypeRoles,
    updateSuite: updateSuiteFromAPI,
    deleteUnit: deleteUnitFromAPI,
    fetchMoreDetails: fetchMoreDetailsFromAPI,
    reRenderKeyPickupPdf: reRenderKeyPickupPdfFromAPI,
  };
};
